import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  ImbalanceSettings,
  ImbalanceSettingsRequestBody,
  ImbalanceSettingsSlim,
} from './types';

export const imbalanceSettingsApi = createApi({
  reducerPath: 'imbalanceSettingsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/imbalance-settings`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['ImbalanceSettings', 'ImbalanceSettingsList'],
  endpoints: builder => ({
    getImbalanceSettingsI: builder.infiniteQuery<
      ImbalanceSettingsSlim[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['ImbalanceSettings', 'ImbalanceSettingsList'],
    }),
    getImbalanceSettingsById: builder.query<ImbalanceSettings, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['ImbalanceSettings'],
    }),
    createImbalanceSettings: builder.mutation<
      ImbalanceSettings,
      ImbalanceSettingsRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'ImbalanceSettingsList',
        { type: 'ImbalanceSettings', id: result?.id },
      ],
    }),
    updateImbalanceSettings: builder.mutation<
      ImbalanceSettings,
      ImbalanceSettingsRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'ImbalanceSettingsList',
        { type: 'ImbalanceSettings', id: result?.id },
      ],
    }),
  }),
});

export const {
  useGetImbalanceSettingsByIdQuery,
  useCreateImbalanceSettingsMutation,
  useUpdateImbalanceSettingsMutation,
} = imbalanceSettingsApi;

export const useGetImbalanceSettingsIInfiniteQuery = normalizeInfiniteQueryData(
  imbalanceSettingsApi.useGetImbalanceSettingsIInfiniteQuery,
);
